import { useCallback } from "react";
import { useInjection } from "@linkfluence/core/di";
import { Platform } from "@linkfluence/models/platform";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import {
  FullInfluencer,
  InfluencerLensFactory,
} from "@radarly/models/influencer";
import {
  InfluencerRepository,
  InfluencerRepositoryUID,
} from "../repositories/InfluencerRepository";

export function useGetInfluencer() {
  const project = useCurrentProject();
  const queryClient = useQueryClient();
  const repository = useInjection<InfluencerRepository>(
    InfluencerRepositoryUID
  );

  const fetchInfluencer = useCallback(
    async (platform: Platform, uid: string) => {
      const cachedInfluencer = queryClient
        .getQueriesData<FullInfluencer>(["/influencer/get"])
        .find(([key, data]) => {
          if (!data?.user) {
            return false;
          }
          const lens = InfluencerLensFactory.create(data.user);
          return lens?.getId() === uid;
        });

      if (cachedInfluencer) {
        return Promise.resolve(cachedInfluencer[1]);
      }

      const influencer = await repository.getInfluencer(
        project.id,
        platform,
        uid
      );

      // Cache fetched influencer
      queryClient.setQueryData(
        [
          "/influencer/get", // key shared with useInfluencerFromUid
          { projectId: project.id, influencerId: influencer.user.uid },
        ],
        influencer
      );
      return influencer;
    },
    [project.id, queryClient, repository]
  );

  return fetchInfluencer;
}
